import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  FetchError,
  FormSubmissionDetailDto,
  FormSubmissionDto,
  optimisticallyUpdateQueriesData,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  formSubmissionGuid: string;
};

const useReviewFormSubmission = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  async function reviewFormSubmissionRequest(variables: Variables) {
    const accessToken = await getAccessToken();

    const response = await fetch(
      `${configuration.apiRootUrl}/form-submissions/${variables.formSubmissionGuid}/review`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        method: "post",
      },
    );

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return useMutation({
    mutationFn: reviewFormSubmissionRequest,
    onMutate: async (variables) => {
      const cachedFormSubmissionDetail = await optimisticallyUpdateQueryData<FormSubmissionDetailDto>(
        queryClient,
        queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid),
        (cachedData) => ({
          ...cachedData,
          reviewedByRecipientDateTimeUtc: DateTime.utc().toISO(),
        }),
      );

      const cachedFormSubmissionsData = await optimisticallyUpdateQueriesData<FormSubmissionDto[]>(
        queryClient,
        queryKeys.formSubmissions.getFormSubmissions(),
        (cachedData) =>
          cachedData.map((formSubmission) => {
            if (formSubmission.formSubmissionGuid === variables.formSubmissionGuid) {
              return {
                ...formSubmission,
                reviewedByRecipientDateTimeUtc: DateTime.utc().toISO(),
              };
            } else {
              return formSubmission;
            }
          }),
        (cachedData) =>
          cachedData.some((formSubmission) => formSubmission.formSubmissionGuid === variables.formSubmissionGuid),
      );

      return { cachedFormSubmissionDetail, cachedFormSubmissionsData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid),
        context?.cachedFormSubmissionDetail,
      );

      if (context?.cachedFormSubmissionsData) {
        context.cachedFormSubmissionsData.forEach((data, key) => queryClient.setQueryData(key, data));
      }
    },
    onSuccess: (_data, variables) => {
      signalR.sendUpdateFormSubmissions(variables.formSubmissionGuid, {
        reviewedByRecipientDateTimeUtc: DateTime.utc().toISO(),
      });

      signalR.sendUpdateFormSubmissionDetail(variables.formSubmissionGuid, {
        reviewedByRecipientDateTimeUtc: DateTime.utc().toISO(),
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid));
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmissions());
    },
  });
};

export { useReviewFormSubmission };
